/**
 * A callable may invite one transparent replay only when its server-side
 * admission check failed before handler code began. Generic network/internal
 * failures are ambiguous: a write or provider call may already have happened.
 */
export interface DatabaseAdmissionRetryDetail {
  schemaVersion: 1;
  reason: 'database-admission-unavailable';
  handlerStarted: false;
  retrySafe: true;
}

type CallableFailure = { code?: unknown; details?: unknown };

const DEFAULT_RETRY_DELAY_MS = 750;

export function isDatabaseAdmissionRetry(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const failure = error as CallableFailure;
  if (failure.code !== 'functions/unavailable' && failure.code !== 'unavailable') return false;
  if (!failure.details || typeof failure.details !== 'object') return false;
  const details = failure.details as Partial<DatabaseAdmissionRetryDetail>;
  return details.schemaVersion === 1
    && details.reason === 'database-admission-unavailable'
    && details.handlerStarted === false
    && details.retrySafe === true;
}

const sleep = (delayMs: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, delayMs));

/** Replay the exact callable payload once, and only for the server's explicit
 * pre-handler admission marker. The caller owns the closure, which prevents a
 * wizard-wide replay of earlier saves. */
export async function callWithDatabaseAdmissionRetry<T>(
  invoke: () => Promise<T>,
  options: { delayMs?: number; sleep?: (delayMs: number) => Promise<void> } = {},
): Promise<T> {
  try {
    return await invoke();
  } catch (error) {
    if (!isDatabaseAdmissionRetry(error)) throw error;
    await (options.sleep ?? sleep)(options.delayMs ?? DEFAULT_RETRY_DELAY_MS);
    return invoke();
  }
}
